import {
  getCartLineHostUnitPrice,
  normalizeCartLineAddOns,
} from '../../OurMenu/cartAddOnUtils';

/**
 * @typedef {import('../../Cart/cartCouponUtils').NormalizedCoupon | null} NormalizedCoupon
 */

/**
 * @typedef {{
 *   id: number,
 *   name: string,
 *   price: number,
 *   hostUnitPrice?: number,
 *   quantity: number,
 *   image?: string,
 *   category?: string,
 *   variantLabel?: string,
 *   addOns?: Array<{ id: number, name: string, price: number, quantity: number }>,
 * }} CheckoutCartItem
 */

/**
 * @typedef {{
 *   form: {
 *     fullName: string,
 *     phone: string,
 *     email: string,
 *     fulfillmentType: 'delivery' | 'pickup',
 *     address1: string,
 *     address2: string,
 *     city: string,
 *     state: string,
 *     pincode: string,
 *     notes: string,
 *     deliveryTime: string,
 *   },
 *   selectedAddressId: number | null,
 *   appliedCoupon: NormalizedCoupon,
 *   cartItems: CheckoutCartItem[],
 *   charges: {
 *     cartTotal: number,
 *     discount: number,
 *     packagingFee: number,
 *     delivery: number,
 *     gstTax: number,
 *     gstPercent: number,
 *     grandTotal: number,
 *   },
 *   distanceKm: number,
 *   savedAt?: number,
 * }} CheckoutPayload
 */

/**
 * @param {Record<string, unknown>} item
 * @returns {CheckoutCartItem | null}
 */
export function normalizeCheckoutCartItem(item) {
  const id = Number(item.id);
  const quantity = Number(item.quantity);
  if (!Number.isFinite(id) || !Number.isFinite(quantity)) {
    return null;
  }
  const addOns = normalizeCartLineAddOns(item.addOns);
  const hostUnitPrice = getCartLineHostUnitPrice({ ...item, addOns });
  if (!Number.isFinite(hostUnitPrice)) {
    return null;
  }
  return {
    id,
    name: String(item.name || 'Item'),
    price: hostUnitPrice,
    hostUnitPrice,
    quantity,
    image: typeof item.image === 'string' ? item.image : undefined,
    category: typeof item.category === 'string' ? item.category : undefined,
    variantLabel:
      typeof item.variantLabel === 'string' && item.variantLabel.trim()
        ? item.variantLabel.trim()
        : undefined,
    addOns,
  };
}

export const PAYMENT_CHECKOUT_STORAGE_KEY = 'digirasa_checkout_payment';

/**
 * @param {unknown} raw
 * @returns {CheckoutPayload | null}
 */
export function normalizeCheckoutPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const o = /** @type {Record<string, unknown>} */ (raw);

  const cartItems = Array.isArray(o.cartItems) ? o.cartItems : null;
  if (!cartItems?.length) return null;

  const charges = o.charges;
  if (!charges || typeof charges !== 'object') return null;
  const c = /** @type {Record<string, unknown>} */ (charges);
  const grandTotal = Number(c.grandTotal);
  if (!Number.isFinite(grandTotal)) return null;

  const formRaw = o.form;
  if (!formRaw || typeof formRaw !== 'object') return null;
  const f = /** @type {Record<string, unknown>} */ (formRaw);

  const normalizedItems = cartItems
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      return normalizeCheckoutCartItem(/** @type {Record<string, unknown>} */ (row));
    })
    .filter(Boolean);

  if (!normalizedItems.length) return null;

  return {
    form: {
      fullName: String(f.fullName || ''),
      phone: String(f.phone || ''),
      email: String(f.email || ''),
      fulfillmentType: f.fulfillmentType === 'pickup' ? 'pickup' : 'delivery',
      address1: String(f.address1 || ''),
      address2: String(f.address2 || ''),
      city: String(f.city || ''),
      state: String(f.state || ''),
      pincode: String(f.pincode || ''),
      notes: String(f.notes || ''),
      deliveryTime: String(f.deliveryTime || 'asap'),
    },
    selectedAddressId:
      o.selectedAddressId != null && Number.isFinite(Number(o.selectedAddressId))
        ? Number(o.selectedAddressId)
        : null,
    appliedCoupon: /** @type {NormalizedCoupon} */ (o.appliedCoupon ?? null),
    cartItems: normalizedItems,
    charges: {
      cartTotal: Number(c.cartTotal) || 0,
      discount: Number(c.discount) || 0,
      packagingFee: Number(c.packagingFee) || 0,
      delivery: Number(c.delivery) || 0,
      gstTax: Number(c.gstTax) || 0,
      gstPercent: Number(c.gstPercent) || 5,
      grandTotal,
    },
    distanceKm: Number.isFinite(Number(o.distanceKm)) ? Number(o.distanceKm) : 0,
    savedAt: Number(o.savedAt) || undefined,
  };
}

/**
 * @param {CheckoutPayload} payload
 */
export function persistCheckoutPayload(payload) {
  try {
    sessionStorage.setItem(PAYMENT_CHECKOUT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

/**
 * @returns {CheckoutPayload | null}
 */
export function readStoredCheckoutPayload() {
  try {
    const raw = sessionStorage.getItem(PAYMENT_CHECKOUT_STORAGE_KEY);
    if (!raw) return null;
    return normalizeCheckoutPayload(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearStoredCheckoutPayload() {
  try {
    sessionStorage.removeItem(PAYMENT_CHECKOUT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @param {unknown} locationState — react-router location.state
 * @returns {CheckoutPayload | null}
 */
export function resolveCheckoutPayload(locationState) {
  if (locationState && typeof locationState === 'object') {
    const fromNav = /** @type {{ checkoutPayload?: unknown }} */ (locationState).checkoutPayload;
    const normalized = normalizeCheckoutPayload(fromNav);
    if (normalized) return normalized;
  }
  return readStoredCheckoutPayload();
}
